/**
 * DocumentUploadModal Component
 * Modal for uploading documents to a property, unit, or lease
 */

import { useState } from 'react';
import { documentsApi } from '../../lib/api';
import { Modal } from '../Modal';
import { readFileIntoMemory } from '@resida/shared';

interface DocumentUploadModalProps {
  isOpen: boolean;
  scope: string;
  propertyId?: string;
  unitId?: string;
  leaseId?: string;
  onSuccess: () => void;
  onClose: () => void;
}

export function DocumentUploadModal({
  isOpen,
  scope,
  propertyId,
  unitId,
  leaseId,
  onSuccess,
  onClose,
}: DocumentUploadModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [tenantHidden, setTenantHidden] = useState(scope === 'UNIT' ? true : false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (!uploading) {
      setName('');
      setDescription('');
      setFile(null);
      setTenantHidden(scope === 'UNIT' ? true : false);
      setError(null);
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('scope', scope);
      if (propertyId) formData.append('propertyId', propertyId);
      if (unitId) formData.append('unitId', unitId);
      if (leaseId) formData.append('leaseId', leaseId);
      formData.append('name', name.trim());
      if (description.trim()) formData.append('description', description.trim());
      if (scope === 'UNIT') {
        formData.append('tenantHidden', tenantHidden ? 'true' : 'false');
      }
      formData.append('file', await readFileIntoMemory(file));

      await documentsApi.upload(formData);
      setName('');
      setDescription('');
      setFile(null);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Upload Document" width="medium" data-testid="document-upload-modal">
      <form onSubmit={handleSubmit} data-testid="document-upload-form">
        {error && (
          <div className="p-3 mb-3 text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-md">
            {error}
          </div>
        )}

        <div className="mb-3">
          <label htmlFor="doc-name" className="block mb-1 text-sm font-medium text-muted-foreground">
            Name *
          </label>
          <input
            id="doc-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-input rounded-md box-border focus:outline-none focus:ring-2 focus:ring-ring"
            required
            aria-required="true"
            placeholder="e.g. Lease Agreement 2024"
            data-testid="document-name-input"
          />
        </div>

        <div className="mb-3">
          <label htmlFor="doc-description" className="block mb-1 text-sm font-medium text-muted-foreground">
            Description
          </label>
          <textarea
            id="doc-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-input rounded-md box-border min-h-[80px] resize-y font-[inherit] focus:outline-none focus:ring-2 focus:ring-ring"
            placeholder="Optional description..."
            data-testid="document-description-input"
          />
        </div>

        {scope === 'UNIT' && (
          <div className="mb-3 flex items-center gap-2">
            <input
              id="doc-tenant-hidden"
              type="checkbox"
              checked={tenantHidden}
              onChange={(e) => setTenantHidden(e.target.checked)}
              className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
              data-testid="document-tenant-hidden-toggle"
            />
            <label htmlFor="doc-tenant-hidden" className="text-sm font-medium text-foreground">
              Hidden from tenant
            </label>
            <span className="text-xs text-muted-foreground">
              Tenant cannot see or download this document
            </span>
          </div>
        )}

        <div className="mb-4">
          <label htmlFor="doc-file" className="block mb-1 text-sm font-medium text-muted-foreground">
            File *
          </label>
          <input
            id="doc-file"
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="w-full px-2 py-1 text-sm border border-input rounded-md box-border focus:outline-none focus:ring-2 focus:ring-ring"
            required
            aria-required="true"
            data-testid="document-file-input"
          />
          {file && (
            <p className="mt-1 mb-0 text-xs text-muted-foreground">
              {file.name} ({formatFileSize(file.size)})
            </p>
          )}
        </div>

        <div className="flex gap-2 justify-end">
          <button
            type="button"
            className="px-4 py-2.5 text-sm font-medium text-foreground bg-transparent border-0 hover:underline disabled:opacity-60 min-h-[44px]"
            onClick={handleClose}
            disabled={uploading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2.5 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 disabled:opacity-60 min-h-[44px]"
            disabled={uploading || !name.trim() || !file}
            data-testid="upload-submit-button"
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default DocumentUploadModal;
